import type { AxiosRequestConfig } from 'axios';

import api from 'api/api.config';

/**
 * Orval axios mutator. All generated TanStack Query hooks route their HTTP
 * calls through this function, so they inherit the shared `api` instance's
 * baseURL, the Okta bearer-token transform, and the response-error interceptor
 * wired in `api/api.config.ts`. We unwrap `AxiosResponse.data` here so
 * generated hooks return `T` instead of `AxiosResponse<T>`.
 *
 * Orval (with `override.query.signal = true`) passes the TanStack Query
 * `AbortSignal` as part of `config`, so cancellation flows through axios
 * natively without a parallel `CancelToken` setup.
 *
 * Note: the OpenAPI spec describes paths absolutely as `/api/...` (Spring
 * controllers all live under that prefix). The shared axios instance already
 * sets `baseURL = '/api'`, so without normalization every generated request
 * would target `/api/api/...`. We strip a single leading `/api` segment from
 * every config URL before delegating to axios — handwritten clients that pass
 * URLs without the prefix continue to work because the strip is a no-op.
 */
const API_PREFIX = '/api';

const stripApiPrefix = (url: string | undefined): string | undefined => {
  if (!url) return url;
  if (url === API_PREFIX) return '/';
  if (url.startsWith(`${API_PREFIX}/`)) return url.slice(API_PREFIX.length);

  return url;
};

/**
 * `axios.defaults.headers.{post,put,patch}` are pinned to `application/json` in
 * `api/api.config.ts`. When a caller hands us `FormData`, axios v1 only strips
 * that default if no `Content-Type` is set at the *request* level, so the
 * multipart body would otherwise ship with `Content-Type: application/json` and
 * no `boundary`. Spring then fails to parse the multipart parts and persists an
 * empty entity (observed as "empty asset/insight PATCH/POST"). Forcing the
 * header to `undefined` tells axios "let the browser/Node adapter compute it",
 * which produces the correct `multipart/form-data; boundary=...`.
 */
const isFormDataBody = (data: unknown): data is FormData =>
  typeof FormData !== 'undefined' && data instanceof FormData;

export const customAxios = <T,>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<T> => {
  const merged: AxiosRequestConfig = {
    ...config,
    ...options,
  };

  merged.url = stripApiPrefix(merged.url);

  if (isFormDataBody(merged.data)) {
    merged.headers = {
      ...(merged.headers ?? {}),
      'Content-Type': undefined,
    };
  }

  return api(merged).then(({ data }) => data as T);
};

export default customAxios;

export type ErrorType<E> = E;
export type BodyType<B> = B;
